import { supabase } from "@/integrations/supabase/client";
import { collectDiagnostics, type DiagnosticPayload } from "./diagnostics";
import { enqueueSupportTicket, initSupportQueueAutoSync, type QueuedTicket } from "./supportQueue";
import { ringLogger } from "./ringBufferLogger";

export interface SubmitSupportParams {
  email: string;
  category: string;
  message: string;
  includeDiagnostics: boolean;
  userId?: string | null;
}

export interface SupportSubmissionResult {
  success: boolean;
  ticketId?: string;
  fingerprint?: string;
  queuedOffline?: boolean;
  error?: string;
}

/**
 * Direct call to Supabase Edge Function to transmit ticket
 */
async function callSubmitFunction(payload: {
  email: string;
  userId?: string | null;
  category: string;
  message: string;
  hasDiagnostics: boolean;
  diagnostics?: DiagnosticPayload;
  platform?: "android" | "web";
}): Promise<{ success: boolean; ticketId?: string; fingerprint?: string; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke("submit-support-ticket", {
      body: payload,
    });

    if (error) {
      throw new Error(error.message || "Failed to invoke support function");
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return {
      success: true,
      ticketId: data?.ticketId,
      fingerprint: data?.fingerprint,
    };
  } catch (err: unknown) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    ringLogger.warn(`[SupportApi] Direct invocation failed: ${errorMsg}`);
    return { success: false, error: errorMsg };
  }
}

/**
 * Submits a support ticket.
 * If the device is offline or the network request fails, automatically queues the payload in IndexedDB.
 */
export async function submitSupportRequest(params: SubmitSupportParams): Promise<SupportSubmissionResult> {
  const { email, category, message, includeDiagnostics, userId } = params;

  let diagnostics: DiagnosticPayload | undefined = undefined;
  if (includeDiagnostics) {
    try {
      diagnostics = await collectDiagnostics(userId);
    } catch (err) {
      ringLogger.warn("[SupportApi] Failed to collect diagnostics:", { error: String(err) });
    }
  }

  const isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

  if (!isOnline) {
    ringLogger.info("[SupportApi] Device is offline. Queuing ticket in IndexedDB.");
    await enqueueSupportTicket({
      userEmail: email,
      userId: userId || null,
      category,
      message,
      hasDiagnostics: includeDiagnostics,
      diagnostics,
    });

    return {
      success: true,
      queuedOffline: true,
    };
  }

  // Attempt online submission
  const result = await callSubmitFunction({
    email,
    userId,
    category,
    message,
    hasDiagnostics: includeDiagnostics,
    diagnostics,
    platform: diagnostics?.platform || "android",
  });

  if (result.success) {
    ringLogger.info("[SupportApi] Ticket successfully transmitted online", { ticketId: result.ticketId });
    return result;
  }

  // If online call failed due to network glitch, queue locally
  ringLogger.info("[SupportApi] Online call failed. Falling back to local offline queue.");
  await enqueueSupportTicket({
    userEmail: email,
    userId: userId || null,
    category,
    message,
    hasDiagnostics: includeDiagnostics,
    diagnostics,
  });

  return {
    success: true,
    queuedOffline: true,
  };
}

/**
 * Sync worker for flushing queued tickets
 */
export async function processQueuedTicket(ticket: QueuedTicket): Promise<{ success: boolean; error?: string }> {
  return await callSubmitFunction({
    email: ticket.userEmail,
    userId: ticket.userId,
    category: ticket.category,
    message: ticket.message,
    hasDiagnostics: ticket.hasDiagnostics,
    diagnostics: ticket.diagnostics,
    platform: ticket.diagnostics?.platform || "android",
  });
}

/**
 * Initialize background sync auto-flusher on app startup
 */
export function startSupportSyncService(): () => void {
  return initSupportQueueAutoSync(processQueuedTicket);
}
